import type { SortKey } from '@/utils/shows';

export interface RangeOption {
  label: string;
  min?: number;
  max?: number;
}

interface FilterPanelProps {
  scoreOptions: RangeOption[];
  yearOptions: RangeOption[];
  sortOptions: { label: string; key: SortKey }[];
  activeScore: number;
  activeYear: number;
  activeSort: SortKey;
  availableTags: string[];
  activeTag: string;
  onScoreChange: (index: number) => void;
  onYearChange: (index: number) => void;
  onSortChange: (key: SortKey) => void;
  onTagChange: (tag: string) => void;
  onReset: () => void;
}

const buildSegmented = (
  options: { label: string }[],
  activeIndex: number,
  onSelect: (index: number) => void,
) => (
  <div className="inline-flex flex-wrap items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
    {options.map((option, index) => (
      <button
        key={option.label}
        type="button"
        onClick={() => onSelect(index)}
        className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
          index === activeIndex
            ? 'bg-primary-500 text-background-50'
            : 'text-foreground-600 hover:bg-background-200'
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

export default function FilterPanel({
  scoreOptions,
  yearOptions,
  sortOptions,
  activeScore,
  activeYear,
  activeSort,
  availableTags,
  activeTag,
  onScoreChange,
  onYearChange,
  onSortChange,
  onTagChange,
  onReset,
}: FilterPanelProps) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5 space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-foreground-600 w-14 shrink-0">评分</span>
          {buildSegmented(scoreOptions, activeScore, onScoreChange)}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-foreground-600 w-14 shrink-0">年份</span>
          {buildSegmented(yearOptions, activeYear, onYearChange)}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-foreground-600 w-14 shrink-0">标签</span>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onTagChange('')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTag === ''
                ? 'bg-secondary-500 text-background-50'
                : 'bg-secondary-100 text-secondary-800 hover:bg-secondary-200'
            }`}
          >
            全部
          </button>
          {availableTags.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => onTagChange(tag)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTag === tag
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-secondary-100 text-secondary-800 hover:bg-secondary-200'
              }`}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1 border-t border-background-200">
        <div className="flex items-center gap-3 pt-3 sm:pt-0">
          <span className="text-sm font-medium text-foreground-600 w-14 shrink-0">排序</span>
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => onSortChange(option.key)}
                className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  option.key === activeSort
                    ? 'bg-accent-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap self-start sm:self-auto"
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      </div>
    </div>
  );
}